/**
 * <ActivationPickerSection> — une section dont le choix se fait AILLEURS (organism).
 *
 * Le patron du funnel inscription, repris par deux des neuf sections du composeur : la ligne
 * RÉSUME ce qui est choisi, la modale (grille de créneaux, catalogue de matières) fait le choix.
 * La section ne possède donc rien — elle rend un résumé et un `onPress`.
 *
 * Trois façons de dire ce qui est choisi, dans cet ordre : les chips des matières, une phrase
 * (« 2 créneaux donnés »), ou le repli quand rien ne l'est (« Choisir les créneaux »).
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Card } from "../../molecules/Card"
import { Icon, type IconName } from "../../atoms/icons/Icon"
import { List } from "../../molecules/List"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { SubjectBadge } from "../../atoms/SubjectBadge"
import { Text } from "../../atoms/typography/Text"
import { type UISubject } from "../../../ports/primary/ui/types"

export type ActivationPickerSectionProps = {
    title: string
    iconName: IconName
    /** Ce qu'on lit quand rien n'est choisi — « Choisir les matières ». */
    placeholder: string
    /** La ligne principale quand un choix existe — « 2 créneaux donnés ». */
    value?: string | null
    /** Les matières choisies, en chips : elles prennent la place de `value`. */
    subjects?: UISubject[]
    /** Seconde ligne, plus discrète — « Lundi 17h-18h · Mercredi 18h-19h ». */
    detail?: string | null
    onPress: () => void
    testID?: string
}

export function ActivationPickerSection(props: ActivationPickerSectionProps) {
    const { title, iconName, placeholder, value, subjects = [], detail, onPress, testID } = props

    return (
        <List title={title} titleIcon={iconName} testID={testID}>
            <Card pressable onPress={onPress} p="3">
                <Row gap="3">
                    <Stack gap="2" style={styles.body}>
                        {subjects.length > 0 ? (
                            <View style={styles.chips}>
                                {subjects.map((subject) => (
                                    <SubjectBadge
                                        key={String(subject.id)}
                                        subject={subject}
                                        showLabel
                                        fullLabel
                                        size="md"
                                    />
                                ))}
                            </View>
                        ) : (
                            <Text weight="medium">{value != null && value !== "" ? value : placeholder}</Text>
                        )}
                        {detail != null && detail !== "" && (
                            <Text size="text-sm" color="text-muted">
                                {detail}
                            </Text>
                        )}
                    </Stack>
                    <Icon name="chevron-right" size={16} color="text-muted" />
                </Row>
            </Card>
        </List>
    )
}

const styles = StyleSheet.create((theme) => ({
    /** Le résumé prend la place restante ; le chevron reste collé à droite. */
    body: { flexShrink: 1, flexGrow: 1 },
    chips: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: theme.spacing["2"],
    },
}))
